import {useState, useEffect, Dispatch, SetStateAction} from 'react';

/**
 * A hook that syncs state with localStorage.
 *
 * @param key - The localStorage key
 * @param defaultValue - The default value if no value is stored
 * @returns A tuple of [value, setValue] similar to useState
 */
export function useLocalStorageState<T>(
    key: string,
    defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
    const [value, setValue] = useState<T>(() => {
        const saved = localStorage.getItem(key);
        if (saved === null) {
            return defaultValue;
        }

        if (typeof defaultValue === 'boolean') {
            return (saved === 'true') as T;
        }

        if (typeof defaultValue === 'number') {
            const parsed = parseFloat(saved);
            return (isNaN(parsed) ? defaultValue : parsed) as T;
        }

        if (typeof defaultValue === 'string') {
            return saved as T;
        }

        try {
            return JSON.parse(saved) as T;
        } catch {
            return defaultValue;
        }
    });

    useEffect(() => {
        if (typeof value === 'boolean' || typeof value === 'number') {
            localStorage.setItem(key, String(value));
        } else if (typeof value === 'string') {
            localStorage.setItem(key, value);
        } else {
            localStorage.setItem(key, JSON.stringify(value));
        }
    }, [key, value]);

    return [value, setValue];
}
